import React from 'react';
import { ReactEditor } from 'slate-react';
import { Element, Location, Transforms } from 'slate';
import { icons } from '@ublend-npm/aulaui-next';
import { insertVoidElement } from '../../lib/commands';
import { AulaPlugin } from '../plugins';
import { assignmentType } from './assignmentType';
import { AssignmentsList } from '../../components/Assignment/AssignmentsList';
import { AssignmentSelected } from '../../components/Assignment/assignmentType';
import { Assignment } from '../../components/Assignment/Assignment';
import {
  AulaEditorContext,
  AulaEditorInstance,
  BlockElement,
} from '../../types';
import { NestedMenuItem } from '../../components/NestedMenu/NestedMenu';

const ASSIGNMENTS_ENDPOINT = 'assignments';

// type guard
export const isAssignmentElement = (
  element: Omit<Element, 'children'>,
): element is BlockElement => element.type === assignmentType;

// this method inserts an assignment block when
// the assignment is selected from the menu list
export const insertAssignment = (
  editor: AulaEditorInstance,
  location: Location,
  assignment: AssignmentSelected,
  context?: Omit<AulaEditorContext, 'entities'>,
) => {
  if (context == null) {
    throw new Error('context undefined');
  }

  const { createEntity } = context;
  // we only use assignment id
  // in the entity, because assignment block
  // will fetch the assignment information from the id
  const entity = createEntity({
    type: 'assignment',
    assignmentId: assignment.id,
    // This is added because the `isUpdating` status checks
    // for null data, and we need to put something here
    // in order to let the editor know this entity is
    // NOT in loading state
    data: { assignmentId: assignment.id },
  });

  // insert at the end of current block
  Transforms.select(editor, location);

  // this method updates the editor entity
  // and respond to undo/redo state of editor
  insertVoidElement(editor, {
    type: assignmentType,
    entity,
  });

  setTimeout(() => {
    ReactEditor.focus(editor);
  }, 100);
};

export const assignment: AulaPlugin = (editableProps, editor, context) => {
  // If the block you're rendering does not contain editable text
  // render a void
  const { isVoid } = editor;
  editor.isVoid = (el) => el?.type === assignmentType || isVoid(el);

  // the return type of an aulaPlugin is `editableProps`
  // for a full list of options, inspect the type EditableProps type
  return {
    ...editableProps,
    blockMenu: {
      ...editableProps.blockMenu,
      'Assignments & Quizzes': {
        ...(editableProps.blockMenu || {})['Assignments & Quizzes'],
        Assignment: {
          listOfAssignments: ({ element }) => (
            <AssignmentsList
              key="assignment-list"
              onSelect={(selectedAssignment) =>
                insertAssignment(
                  editor,
                  ReactEditor.findPath(editor, element),
                  selectedAssignment,
                  context,
                )
              }
              assignmentsEndpoint={ASSIGNMENTS_ENDPOINT}
            />
          ),
        },
      },
    },
    renderSubMenuItem: (props) => {
      const { text } = props;
      return text && ['Assignment', 'Assignments & Quizzes'].includes(text) ? (
        <NestedMenuItem
          leftIcon={<icons.AssignmentIcon />}
          rightIcon={<icons.ArrowRight />}
          {...props}
        />
      ) : (
        editableProps?.renderSubMenuItem?.(props) || (
          <NestedMenuItem {...props} />
        )
      );
    },
    renderElement: (props) => {
      const { element, attributes, children } = props;
      if (isAssignmentElement(element)) {
        return (
          <div {...attributes}>
            <Assignment
              element={element}
              assignmentsEndpoint={ASSIGNMENTS_ENDPOINT}
            />
            {children}
          </div>
        );
      }
      return editableProps.renderElement?.(props) || <p />;
    },
  };
};
